import React, { useState, useMemo } from 'react';
import SafeIcon from '../../common/SafeIcon';
import { getAllForms } from '../../forms';
import * as FiIcons from 'react-icons/fi';

const {
  FiSearch,
  FiFilter,
  FiChevronDown,
  FiChevronUp,
  FiCheckSquare,
  FiSquare,
  FiEye,
  FiEdit,
  FiTrash2,
  FiDownload,
  FiColumns,
  FiCalendar,
  FiCheckCircle,
  FiClock,
  FiFileText,
  FiUser,
  FiHome,
  FiSliders,
  FiRefreshCw
} = FiIcons;

/**
 * HighDensityDataGrid
 * Unified high-density tabular view for SaaS certificates with:
 * - Faceted multi-filter bar (Date Range, Form Type, Status, Customer)
 * - Fast full-text search with keyword highlighting
 * - Column visibility toggling & table density adjustment (Compact / Normal / Relaxed)
 * - Multi-select Batch Actions (Batch PDF export, batch status change, batch archive)
 * - Quick click row inspection (Slide-Over Trigger)
 */
export const HighDensityDataGrid = ({
  records = [],
  loading = false,
  onInspectRecord,
  onEditRecord,
  onDeleteRecord,
  onBatchStatusChange,
  onBatchDelete,
  onRefresh
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [formTypeFilter, setFormTypeFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all'); // 'all' | 'today' | 'week' | 'month'
  const [density, setDensity] = useState('normal'); // 'compact' | 'normal' | 'relaxed'
  const [sortField, setSortField] = useState('updatedAt');
  const [sortAsc, setSortAsc] = useState(false);

  // Selected row IDs for batch operations
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Visible columns configuration
  const [visibleColumns, setVisibleColumns] = useState({
    serial: true,
    formType: true,
    customer: true,
    address: true,
    status: true,
    date: true,
    actions: true
  });
  const [showColumnDropdown, setShowColumnDropdown] = useState(false);

  const availableForms = getAllForms();

  // Filter & Search Logic
  const filteredRecords = useMemo(() => {
    return records.filter((rec) => {
      // 1. Status Filter
      if (statusFilter !== 'all' && rec.status !== statusFilter) {
        return false;
      }

      // 2. Form Type Filter
      if (formTypeFilter !== 'all' && rec.formId !== formTypeFilter) {
        return false;
      }

      // 3. Date Range Filter
      if (dateFilter !== 'all') {
        const itemDate = new Date(rec.updatedAt || rec.createdAt);
        const now = new Date();
        if (dateFilter === 'today') {
          const isToday = itemDate.toDateString() === now.toDateString();
          if (!isToday) return false;
        } else if (dateFilter === 'week') {
          const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          if (itemDate < sevenDaysAgo) return false;
        } else if (dateFilter === 'month') {
          const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          if (itemDate < thirtyDaysAgo) return false;
        }
      }

      // 4. Full Text Search
      if (searchTerm.trim()) {
        const query = searchTerm.toLowerCase();
        const formConfig = availableForms.find((f) => f.id === rec.formId);
        const formName = formConfig?.name?.toLowerCase() || '';
        const instanceId = (rec.instanceId || '').toLowerCase();
        const serialNumber = (rec.serialNumber || '').toLowerCase();
        const dataStr = JSON.stringify(rec.data || {}).toLowerCase();

        return (
          instanceId.includes(query) ||
          serialNumber.includes(query) ||
          formName.includes(query) ||
          dataStr.includes(query)
        );
      }

      return true;
    });
  }, [records, statusFilter, formTypeFilter, dateFilter, searchTerm, availableForms]);

  // Sort logic
  const sortedRecords = useMemo(() => {
    return [...filteredRecords].sort((a, b) => {
      let valA = a[sortField];
      let valB = b[sortField];

      if (sortField === 'updatedAt' || sortField === 'createdAt') {
        valA = new Date(valA || 0).getTime();
        valB = new Date(valB || 0).getTime();
      }

      if (valA < valB) return sortAsc ? -1 : 1;
      if (valA > valB) return sortAsc ? 1 : -1;
      return 0;
    });
  }, [filteredRecords, sortField, sortAsc]);

  // Batch selection handlers
  const handleSelectAll = () => {
    if (selectedIds.size === sortedRecords.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(sortedRecords.map((r) => r.id)));
    }
  };

  const handleToggleRow = (id, e) => {
    e?.stopPropagation();
    const next = new Set(selectedIds);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    setSelectedIds(next);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortAsc(!sortAsc);
    } else {
      setSortField(field);
      setSortAsc(false);
    }
  };

  const isAllSelected = sortedRecords.length > 0 && selectedIds.size === sortedRecords.length;
  const isIndeterminate = selectedIds.size > 0 && selectedIds.size < sortedRecords.length;

  return (
    <div className="flex flex-col h-full space-y-3">
      {/* Faceted Filter Bar */}
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-3 shadow-sm flex flex-wrap items-center justify-between gap-3">
        {/* Search & Main Filters */}
        <div className="flex flex-wrap items-center gap-2.5 flex-1 min-w-[280px]">
          {/* Search Box */}
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search serial, customer, address or equipment..."
              className="w-full pl-9 pr-4 py-1.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-xs text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>

          {/* Form Type Filter */}
          <select
            value={formTypeFilter}
            onChange={(e) => setFormTypeFilter(e.target.value)}
            className="text-xs bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 rounded-lg px-2.5 py-1.5 focus:outline-none"
          >
            <option value="all">All Certificate Types</option>
            {availableForms.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </select>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="text-xs bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 rounded-lg px-2.5 py-1.5 focus:outline-none"
          >
            <option value="all">All Statuses</option>
            <option value="draft">Drafts</option>
            <option value="completed">Completed</option>
            <option value="archived">Archived</option>
          </select>

          {/* Date Filter */}
          <select
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            className="text-xs bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 rounded-lg px-2.5 py-1.5 focus:outline-none"
          >
            <option value="all">All Time</option>
            <option value="today">Today</option>
            <option value="week">Past 7 Days</option>
            <option value="month">Past 30 Days</option>
          </select>
        </div>

        {/* Density & Column Controls */}
        <div className="flex items-center gap-2">
          {onRefresh && (
            <button
              onClick={onRefresh}
              className="p-1.5 text-slate-500 hover:text-slate-800 dark:hover:text-slate-200 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800"
              title="Refresh Records"
            >
              <SafeIcon icon={FiRefreshCw} className="w-3.5 h-3.5" />
            </button>
          )}

          {/* Density toggle buttons */}
          <div className="flex rounded-lg border border-slate-200 dark:border-slate-700 p-0.5 bg-slate-50 dark:bg-slate-800">
            <button
              onClick={() => setDensity('compact')}
              className={`px-2 py-1 text-[11px] font-medium rounded ${
                density === 'compact'
                  ? 'bg-white dark:bg-slate-700 text-blue-600 dark:text-blue-400 shadow-sm'
                  : 'text-slate-500'
              }`}
              title="High Density (Compact Rows)"
            >
              Compact
            </button>
            <button
              onClick={() => setDensity('normal')}
              className={`px-2 py-1 text-[11px] font-medium rounded ${
                density === 'normal'
                  ? 'bg-white dark:bg-slate-700 text-blue-600 dark:text-blue-400 shadow-sm'
                  : 'text-slate-500'
              }`}
              title="Balanced Density"
            >
              Normal
            </button>
          </div>

          {/* Column Visibility Menu */}
          <div className="relative">
            <button
              onClick={() => setShowColumnDropdown(!showColumnDropdown)}
              className="flex items-center gap-1 px-2.5 py-1.5 text-xs text-slate-600 dark:text-slate-300 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-100"
            >
              <SafeIcon icon={FiColumns} className="w-3.5 h-3.5" />
              <span>Columns</span>
            </button>

            {showColumnDropdown && (
              <div className="absolute right-0 top-full mt-1.5 w-48 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl shadow-xl p-2 z-40 text-xs">
                <p className="font-semibold text-slate-400 uppercase text-[10px] px-2 py-1">Toggle Columns</p>
                {Object.keys(visibleColumns).map((col) => (
                  <label
                    key={col}
                    className="flex items-center gap-2 px-2 py-1.5 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-lg cursor-pointer capitalize text-slate-700 dark:text-slate-300"
                  >
                    <input
                      type="checkbox"
                      checked={visibleColumns[col]}
                      onChange={() =>
                        setVisibleColumns((prev) => ({ ...prev, [col]: !prev[col] }))
                      }
                      className="rounded text-blue-600"
                    />
                    <span>{col}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Batch Actions Floating Ribbon */}
      {selectedIds.size > 0 && (
        <div className="bg-blue-600 text-white px-4 py-2 rounded-xl shadow-lg flex items-center justify-between text-xs animate-in fade-in slide-in-from-top-2">
          <div className="flex items-center gap-2 font-medium">
            <span className="w-5 h-5 rounded-full bg-white text-blue-600 flex items-center justify-center font-bold text-[11px]">
              {selectedIds.size}
            </span>
            <span>records selected</span>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                onBatchStatusChange?.(Array.from(selectedIds), 'completed');
                setSelectedIds(new Set());
              }}
              className="px-2.5 py-1 bg-white/20 hover:bg-white/30 rounded-lg font-medium transition-colors"
            >
              Mark Completed
            </button>
            <button
              onClick={() => {
                onBatchStatusChange?.(Array.from(selectedIds), 'draft');
                setSelectedIds(new Set());
              }}
              className="px-2.5 py-1 bg-white/20 hover:bg-white/30 rounded-lg font-medium transition-colors"
            >
              Mark Draft
            </button>
            <button
              onClick={() => {
                if (window.confirm(`Delete ${selectedIds.size} selected certificates?`)) {
                  onBatchDelete?.(Array.from(selectedIds));
                  setSelectedIds(new Set());
                }
              }}
              className="px-2.5 py-1 bg-rose-500 hover:bg-rose-600 rounded-lg font-medium transition-colors"
            >
              Batch Delete
            </button>
            <button
              onClick={() => setSelectedIds(new Set())}
              className="px-2 py-1 text-blue-100 hover:text-white"
            >
              Clear
            </button>
          </div>
        </div>
      )}

      {/* Table Grid Container */}
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden shadow-sm flex-1 flex flex-col min-h-0">
        <div className="overflow-x-auto flex-1">
          <table className={`w-full text-left border-collapse density-${density}`}>
            {/* Table Header */}
            <thead className="bg-slate-50 dark:bg-slate-800/80 text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider border-b border-slate-200 dark:border-slate-800 sticky top-0 z-10 select-none">
              <tr>
                <th className="p-3 w-10 text-center">
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    ref={(el) => el && (el.indeterminate = isIndeterminate)}
                    onChange={handleSelectAll}
                    className="rounded text-blue-600 cursor-pointer"
                  />
                </th>

                {visibleColumns.serial && (
                  <th
                    className="p-3 cursor-pointer hover:text-slate-900 dark:hover:text-slate-200"
                    onClick={() => handleSort('instanceId')}
                  >
                    <div className="flex items-center gap-1">
                      <span>Ref / Identifier</span>
                      {sortField === 'instanceId' && (
                        <SafeIcon icon={sortAsc ? FiChevronUp : FiChevronDown} className="w-3.5 h-3.5 text-blue-500" />
                      )}
                    </div>
                  </th>
                )}

                {visibleColumns.formType && (
                  <th
                    className="p-3 cursor-pointer hover:text-slate-900 dark:hover:text-slate-200"
                    onClick={() => handleSort('formId')}
                  >
                    <div className="flex items-center gap-1">
                      <span>Certificate Type</span>
                      {sortField === 'formId' && (
                        <SafeIcon icon={sortAsc ? FiChevronUp : FiChevronDown} className="w-3.5 h-3.5 text-blue-500" />
                      )}
                    </div>
                  </th>
                )}

                {visibleColumns.customer && <th className="p-3">Customer / Landlord</th>}
                {visibleColumns.address && <th className="p-3">Site Address</th>}

                {visibleColumns.status && (
                  <th
                    className="p-3 cursor-pointer hover:text-slate-900 dark:hover:text-slate-200"
                    onClick={() => handleSort('status')}
                  >
                    <div className="flex items-center gap-1">
                      <span>Status</span>
                      {sortField === 'status' && (
                        <SafeIcon icon={sortAsc ? FiChevronUp : FiChevronDown} className="w-3.5 h-3.5 text-blue-500" />
                      )}
                    </div>
                  </th>
                )}

                {visibleColumns.date && (
                  <th
                    className="p-3 cursor-pointer hover:text-slate-900 dark:hover:text-slate-200"
                    onClick={() => handleSort('updatedAt')}
                  >
                    <div className="flex items-center gap-1">
                      <span>Updated</span>
                      {sortField === 'updatedAt' && (
                        <SafeIcon icon={sortAsc ? FiChevronUp : FiChevronDown} className="w-3.5 h-3.5 text-blue-500" />
                      )}
                    </div>
                  </th>
                )}

                {visibleColumns.actions && <th className="p-3 text-right">Actions</th>}
              </tr>
            </thead>

            {/* Table Body */}
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-xs">
              {loading ? (
                <tr>
                  <td colSpan={8} className="p-8 text-center text-slate-400">
                    <div className="inline-block animate-spin w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full mb-2"></div>
                    <p>Loading certificates...</p>
                  </td>
                </tr>
              ) : sortedRecords.length === 0 ? (
                <tr>
                  <td colSpan={8} className="p-12 text-center text-slate-400">
                    <SafeIcon icon={FiFileText} className="w-10 h-10 mx-auto mb-2 opacity-30" />
                    <p className="font-semibold text-slate-600 dark:text-slate-300">No certificates found</p>
                    <p className="text-[11px] text-slate-400 mt-0.5">
                      {searchTerm || statusFilter !== 'all' || formTypeFilter !== 'all'
                        ? 'Try clearing your filters or search term.'
                        : 'Click "New Certificate" to generate your first document.'}
                    </p>
                  </td>
                </tr>
              ) : (
                sortedRecords.map((rec) => {
                  const formConfig = availableForms.find((f) => f.id === rec.formId);
                  const data = rec.data || {};
                  const customer = data.customerInfo || data.clientDetails || {};
                  const jobAddress = data.jobAddress || data.siteAddress || data.propertyDetails || {};
                  const isSelected = selectedIds.has(rec.id);

                  return (
                    <tr
                      key={rec.id}
                      onClick={() => onInspectRecord?.(rec)}
                      className={`hover:bg-blue-50/50 dark:hover:bg-slate-800/60 cursor-pointer transition-colors ${
                        isSelected ? 'bg-blue-50 dark:bg-blue-950/30' : ''
                      }`}
                    >
                      {/* Selection Box */}
                      <td className="p-3 text-center" onClick={(e) => handleToggleRow(rec.id, e)}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => {}}
                          className="rounded text-blue-600 cursor-pointer"
                        />
                      </td>

                      {/* Identifier */}
                      {visibleColumns.serial && (
                        <td className="p-3 font-semibold text-slate-900 dark:text-slate-100">
                          <div className="flex items-center gap-1.5">
                            <SafeIcon icon={FiFileText} className="w-3.5 h-3.5 text-slate-400" />
                            <span className="truncate max-w-[150px]">{rec.instanceId || `#${rec.id}`}</span>
                          </div>
                        </td>
                      )}

                      {/* Certificate Type */}
                      {visibleColumns.formType && (
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <span className="text-base">{formConfig?.icon || '📄'}</span>
                            <div className="truncate max-w-[180px]">
                              <p className="font-medium text-slate-800 dark:text-slate-200 truncate">
                                {formConfig?.name || rec.formId}
                              </p>
                              <p className="text-[10px] text-slate-400">{formConfig?.category || 'Standard'}</p>
                            </div>
                          </div>
                        </td>
                      )}

                      {/* Customer */}
                      {visibleColumns.customer && (
                        <td className="p-3 text-slate-600 dark:text-slate-300">
                          <div className="truncate max-w-[140px]">
                            <p className="font-medium truncate">{customer.name || customer.customerName || '—'}</p>
                            <p className="text-[10px] text-slate-400 truncate">{customer.telephone || customer.email || ''}</p>
                          </div>
                        </td>
                      )}

                      {/* Address */}
                      {visibleColumns.address && (
                        <td className="p-3 text-slate-600 dark:text-slate-300">
                          <div className="truncate max-w-[160px]">
                            <p className="truncate">{jobAddress.line1 || jobAddress.address || '—'}</p>
                            <p className="text-[10px] text-slate-400 truncate">{jobAddress.postcode || ''}</p>
                          </div>
                        </td>
                      )}

                      {/* Status Badge */}
                      {visibleColumns.status && (
                        <td className="p-3">
                          <span
                            className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-semibold ${
                              rec.status === 'completed'
                                ? 'bg-emerald-100 dark:bg-emerald-950/50 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800'
                                : 'bg-amber-100 dark:bg-amber-950/50 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800'
                            }`}
                          >
                            <span
                              className={`w-1.5 h-1.5 rounded-full ${
                                rec.status === 'completed' ? 'bg-emerald-500' : 'bg-amber-500'
                              }`}
                            ></span>
                            {rec.status === 'completed' ? 'Completed' : 'Draft'}
                          </span>
                        </td>
                      )}

                      {/* Date */}
                      {visibleColumns.date && (
                        <td className="p-3 text-slate-500 dark:text-slate-400 text-[11px]">
                          {new Date(rec.updatedAt || rec.createdAt).toLocaleDateString()}
                        </td>
                      )}

                      {/* Actions */}
                      {visibleColumns.actions && (
                        <td className="p-3 text-right">
                          <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                            <button
                              onClick={() => onInspectRecord?.(rec)}
                              className="p-1 text-slate-400 hover:text-blue-600 rounded hover:bg-slate-100 dark:hover:bg-slate-800"
                              title="Quick Inspect"
                            >
                              <SafeIcon icon={FiEye} className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => onEditRecord?.(rec)}
                              className="p-1 text-slate-400 hover:text-blue-600 rounded hover:bg-slate-100 dark:hover:bg-slate-800"
                              title="Open Editor"
                            >
                              <SafeIcon icon={FiEdit} className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => onDeleteRecord?.(rec.id)}
                              className="p-1 text-slate-400 hover:text-rose-600 rounded hover:bg-slate-100 dark:hover:bg-slate-800"
                              title="Delete"
                            >
                              <SafeIcon icon={FiTrash2} className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Table Footer / Summary */}
        <div className="p-3 bg-slate-50 dark:bg-slate-800/60 border-t border-slate-200 dark:border-slate-800 flex items-center justify-between text-[11px] text-slate-500 dark:text-slate-400">
          <span>
            Showing <strong className="text-slate-700 dark:text-slate-300">{sortedRecords.length}</strong> of{' '}
            <strong className="text-slate-700 dark:text-slate-300">{records.length}</strong> certificates
          </span>
          <span>IndexedDB Local Store Scoped</span>
        </div>
      </div>
    </div>
  );
};

export default HighDensityDataGrid;
